import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import { StockingLoader } from '@wix/gg-components';
import { useOrchestratorNavigation } from '@wix/gg-components/orchestrator';
import { useSelectedCategory } from 'stores/appStore';
import { useSetupStoreInitialization } from '../../api';
import styles from './SetupStoreContent.scss';
import { Text } from '@wix/design-system';
import { getRefProvider } from 'config/providers';
import { RefProvider } from 'config/enums';

const MODALYST_PRODUCT_COUNT = 20;
const isZenDrop = getRefProvider() === RefProvider.ZenDrop;

export function SetupStoreContent() {
  const { t } = useTranslation();
  const { next } = useOrchestratorNavigation();
  const category = useSelectedCategory();
  const {
    mutate: initializeSite,
    isPending: isInitializing,
    isSuccess: isInitializationComplete,
  } = useSetupStoreInitialization();
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const initializedCategoryRef = useRef<typeof category>();

  const isReady = isInitializationComplete && isAnimationComplete;

  function renderContent(count: number) {
    if (isZenDrop) {
      return <Text size="small">{t('store_name_loading.content.zendrop')}</Text>;
    }
    return (
      <Text size="small">
        <Trans
          i18nKey="store_name_loading.content"
          values={{ count }}
          components={{ bold: <Text weight="bold" as="span" /> }}
        />
      </Text>
    );
  }

  useEffect(() => {
    if (!category || initializedCategoryRef.current === category) {
      return;
    }
    // Ref guards against re-initialization if the effect fires more than once for the same category
    initializedCategoryRef.current = category;
    initializeSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (isReady) {
      next();
    }
    // next is a stable orchestrator reference — intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  return (
    <div className={styles.loadingContainer}>
      <StockingLoader
        contentRenderer={renderContent}
        isLoading={isInitializing}
        title={t('store_name_loading.title')}
        subtitle={t('store_name_loading.subtitle')}
        statusMessages={[
          t('store_name_loading.status.analyzing_market'),
          t('store_name_loading.status.optimizing_selection'),
          t('store_name_loading.status.finalizing_recommendations'),
        ]}
        totalCount={isZenDrop ? 0 : MODALYST_PRODUCT_COUNT}
        onAnimationComplete={() => setIsAnimationComplete(true)}
        progressUpdateInterval={300}
        progressIncrement={2}
      />
    </div>
  );
}
